import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  ForwardedRef
} from 'react';
import { LoadingBalls, Overlay, Text } from '@moda/om';
import { usePrevious } from '@moda/portal-stanchions';
import { reportError } from '../../../lib/reportError';
import { isGenericError } from '../../../lib/error';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useAddressesQuery, RestAddressAttributes, RestAddress } from '../../../hooks/useAddress';
import { Box } from '../../Box';
import { ShippingAddressPreview } from '../ShippingAddressPreview';
import { ShippingAddressForm } from '../ShippingAddressForm';
import { ShippingAddressList } from '../ShippingAddressList';
import { tc } from '../../../lib/trackingContext';
import { CheckoutStep } from '../../../hooks/useCheckout/useCheckoutStepTracking';
import { Loading } from '../../Loading';

export enum Mode {
  Resting,
  Selecting,
  Creating,
  Editing,
  Saving
}

type Props = {
  theme?: 'simple' | 'box';
  source?: string;
  readOnly?: boolean;
  disabledAddressIds?: string[];
  defaultAddress?: RestAddressAttributes;
  defaultAddressId?: string;
  onModeChange?: (previousMode: Mode | undefined, nextMode: Mode) => void;
  onSubmit: (address: RestAddress) => Promise<void>;
  allowEdit?: boolean;
};

export type RefInterface = {
  change: () => void;
};

export const ShippingAddressSelector = forwardRef(
  (
    {
      theme = 'simple',
      source,
      readOnly,
      disabledAddressIds,
      defaultAddressId,
      defaultAddress,
      onSubmit,
      onModeChange,
      allowEdit = true
    }: Props,
    ref: ForwardedRef<RefInterface>
  ) => {
    const { showErrorModal } = useErrorModal();
    const { addresses: allAddresses, fetched, error } = useAddressesQuery();

    const addresses = useMemo(
      () =>
        source
          ? allAddresses.filter(address => address.attributes.source === source)
          : allAddresses,
      [allAddresses, source]
    );

    const defaultMode = useMemo(() => {
      if (fetched && addresses.length === 0) {
        return Mode.Creating;
      }

      if (defaultAddressId || defaultAddress) {
        return Mode.Resting;
      }

      return Mode.Selecting;
    }, [addresses.length, defaultAddress, defaultAddressId, fetched]);

    const [mode, setMode] = useState(defaultMode);
    const [editingAddressId, setEditingAddressId] = useState<string>();

    const previousMode = usePrevious(mode);

    const selectedAddress =
      defaultAddress ?? addresses.find(address => address.id === defaultAddressId)?.attributes;
    const editingAddress = addresses.find(address => address.id === editingAddressId);

    const handleSubmit = useCallback(
      async (address: RestAddress) => {
        try {
          await onSubmit(address);
          tc.track('Shipping Address Selected', {
            addToScopeOnce: {
              checkoutStep: CheckoutStep.ShippingAddress,
              address
            },
            addToTrackingEvent: {
              label: address.id
            }
          });
        } catch (error) {
          reportError('SHIPPING_ADDRESS_SELECTOR_SUBMIT', error);
          showErrorModal(isGenericError(error) ? error.toString() : null);
        }
        setMode(Mode.Resting);
      },
      [showErrorModal, onSubmit]
    );

    const handleChange = useCallback(
      () => !readOnly && setMode(addresses.length > 0 ? Mode.Selecting : Mode.Creating),
      [readOnly, addresses]
    );

    useImperativeHandle(ref, () => ({
      change: handleChange
    }));

    useEffect(() => {
      onModeChange?.(previousMode, mode);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    if (error) throwQueryError(error);

    if (!fetched) return <Loading />;

    if (mode === Mode.Resting && selectedAddress)
      return (
        <Box borderless={theme === 'simple'}>
          <ShippingAddressPreview
            attributes={selectedAddress}
            onChange={readOnly ? undefined : handleChange}
          />
        </Box>
      );

    if (mode === Mode.Creating)
      return (
        <ShippingAddressForm
          submitButtonLabel="Continue"
          onCancel={addresses.length === 0 ? undefined : () => setMode(Mode.Selecting)}
          onSave={address => address && handleSubmit(address)}
        />
      );

    if (mode === Mode.Editing)
      return (
        <ShippingAddressForm
          address={editingAddress}
          submitButtonLabel="Continue"
          onCancel={() => setMode(Mode.Selecting)}
          onSave={address => address && handleSubmit(address)}
        />
      );

    return (
      <>
        <ShippingAddressList
          addresses={addresses}
          disabledAddressIds={disabledAddressIds}
          defaultAddressId={defaultAddressId}
          onAdd={() => setMode(Mode.Creating)}
          onEdit={
            allowEdit
              ? addressId => {
                  setEditingAddressId(addressId);
                  setMode(Mode.Editing);
                }
              : undefined
          }
          onCancel={() => setMode(Mode.Resting)}
          onChange={addressId => {
            const address = addresses.find(address => address.id === addressId);
            if (!address) return;
            setMode(Mode.Saving);
            handleSubmit(address);
          }}
        />
        {mode === Mode.Saving && (
          <Overlay>
            <LoadingBalls />

            <Text treatment="eyebrow">Saving</Text>
          </Overlay>
        )}
      </>
    );
  }
);
